import React, { useMemo, useState } from 'react';
import { PT_MIN_WIDTH } from '../../patients-list/config';
import type { PtCardSize, PtDirectoryPatient } from '../../patients-list/types';
import { ptContactMatches, type PtContactFilter } from '../../patients-list/contactFlags';
import { ptDirectoryRouteId, ptSearchPatients } from '../../patients-list/utils';
import { cn } from '../../lib/cn';
import { PatientsDirectorySection } from './patients-directory/PatientsDirectorySection';
import { PatientsSearch } from './patients-directory/PatientsSearch';
import { PatientsSearchResults } from './patients-directory/PatientsSearchResults';
import { PatientsSummaryBar } from './patients-directory/PatientsSummaryBar';

export interface PatientsListProps {
  today?: PtDirectoryPatient[];
  tomorrow?: PtDirectoryPatient[];
  onSelectPatient: (id: string) => void;
  cardSize?: PtCardSize;
  showPhotos?: boolean;
  className?: string;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  searchResults?: PtDirectoryPatient[];
  isSearchLoading?: boolean;
  /** Amendment 32 §5 — the Contact filter, from `/patients?contact=…`. */
  contactFilter?: PtContactFilter | undefined;
  onContactFilter?: (f: PtContactFilter | null) => void;
}

export function PatientsList({
  today = [],
  tomorrow = [],
  onSelectPatient,
  cardSize = 'regular',
  showPhotos = true,
  className,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  isSearchLoading = false,
  contactFilter,
  onContactFilter,
}: PatientsListProps) {
  const [internalQuery, setInternalQuery] = useState('');
  const query = searchQuery ?? internalQuery;
  const setQuery = onSearchQueryChange ?? setInternalQuery;
  const minWidth = PT_MIN_WIDTH[cardSize] ?? PT_MIN_WIDTH.regular;
  const isSearching = query.trim().length > 0;
  const contactRows = contactFilter !== undefined ? ptContactMatches(contactFilter) : [];
  const useApiSearch = searchResults !== undefined;

  const results = useMemo(() => {
    if (!isSearching) return [];
    if (useApiSearch) return searchResults;
    return ptSearchPatients([...today, ...tomorrow], query);
  }, [isSearching, useApiSearch, searchResults, query, today, tomorrow]);

  const handleSelect = (patient: PtDirectoryPatient) => {
    onSelectPatient(ptDirectoryRouteId(patient));
  };

  return (
    <div className={cn('pp-scroll flex min-h-0 flex-1 flex-col overflow-y-auto', className)}>
      <div className="pp-fade max-w-[1640px] px-[30px] pb-[18px] pt-2">
        <PatientsSearch query={query} onQueryChange={setQuery} />
        {onContactFilter ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-3)]">Contact</span>
            {([['texts-off', 'Texts off'], ['stopped-topic', 'Stopped a topic']] as const).map(([key, label]) => {
              const on = contactFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { onContactFilter(on ? null : key); }}
                  className={cn(
                    'rounded-full border px-3 py-1 text-[12px] font-semibold',
                    on ? 'border-[var(--ai-3)] bg-[var(--ai-tint)] text-[var(--ai-3)]' : 'border-[var(--cream-2)] text-[var(--ink-2)] hover:bg-[var(--cream-1)]',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {contactFilter !== undefined && !isSearching ? (
        <div className="pp-fade max-w-[1640px] px-[30px]">
          <div className="mb-2 text-[13px] font-bold text-[var(--ink-0)]">
            {contactFilter === 'texts-off' ? 'Texts off' : 'Stopped a topic'}{' '}
            <span className="text-[var(--ink-4)]">{contactRows.length}</span>
          </div>
          <div className="flex flex-col gap-2">
            {contactRows.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onSelectPatient(p.id); }}
                className="flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3 text-left shadow-[var(--sh-1)] hover:border-[var(--ai-3)]"
              >
                <span className="text-[13.5px] font-semibold text-[var(--ink-0)]">{p.name}</span>
                <span className="font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">{p.phone}</span>
                {p.textsOff !== undefined ? (
                  <span className="rounded-full bg-[var(--terracotta-tint)] px-2 py-0.5 text-[11px] font-semibold text-[var(--terracotta)]">
                    Texts off · since {p.textsOff.since} · {p.textsOff.how}
                  </span>
                ) : null}
                {p.stoppedTopic !== undefined ? (
                  <span className="rounded-full bg-[var(--gold-tint)] px-2 py-0.5 text-[11px] font-semibold text-[var(--gold)]">
                    Stopped: {p.stoppedTopic.topic} · since {p.stoppedTopic.since}
                  </span>
                ) : null}
              </button>
            ))}
            {contactRows.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-[var(--ink-3)]">Nobody in this filter.</div>
            ) : null}
          </div>
        </div>
      ) : isSearching ? (
        <PatientsSearchResults
          query={query.trim()}
          patients={results}
          minWidth={minWidth}
          showPhotos={showPhotos}
          isLoading={useApiSearch ? isSearchLoading : false}
          onSelectPatient={handleSelect}
        />
      ) : (
        <>
          <div className="pp-fade max-w-[1640px] px-[30px]">
            <PatientsSummaryBar patients={today} />
          </div>
          <PatientsDirectorySection
            eyebrow="Today"
            title="Today’s patients"
            patients={today}
            minWidth={minWidth}
            showPhotos={showPhotos}
            onSelectPatient={handleSelect}
          />
          <PatientsDirectorySection
            eyebrow="Tomorrow"
            title="Tomorrow’s patients"
            patients={tomorrow}
            minWidth={minWidth}
            showPhotos={showPhotos}
            onSelectPatient={handleSelect}
          />
        </>
      )}

      <div className="h-8 shrink-0" aria-hidden />
    </div>
  );
}
